import * as React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type Variant = "card" | "row" | "hero" | "card-grid";

export interface LoadingBlockProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: Variant;
  count?: number;
}

function One({ variant }: { variant: Variant }) {
  if (variant === "hero") {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-14 w-3/4" />
        <Skeleton className="h-14 w-2/3" />
        <Skeleton className="h-5 w-1/2" />
        <div className="flex gap-3 pt-2">
          <Skeleton className="h-12 w-40 rounded-full" />
          <Skeleton className="h-12 w-32 rounded-full" />
        </div>
      </div>
    );
  }
  if (variant === "row") {
    return (
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    );
  }
  // card
  return (
    <div className="space-y-3 rounded-lg border border-border-1 bg-surface-2 p-4">
      <Skeleton className="h-32 w-full rounded-md" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  );
}

export const LoadingBlock = React.forwardRef<HTMLDivElement, LoadingBlockProps>(
  ({ variant = "card", count, className, ...props }, ref) => {
    if (variant === "card-grid") {
      const n = count ?? 6;
      return (
        <div ref={ref} className={cn("grid gap-4 sm:grid-cols-2 lg:grid-cols-3", className)} {...props}>
          {Array.from({ length: n }).map((_, i) => (
            <One key={i} variant="card" />
          ))}
        </div>
      );
    }
    if (count && count > 1) {
      return (
        <div ref={ref} className={cn("space-y-3", className)} {...props}>
          {Array.from({ length: count }).map((_, i) => (
            <One key={i} variant={variant} />
          ))}
        </div>
      );
    }
    return (
      <div ref={ref} className={className} {...props}>
        <One variant={variant} />
      </div>
    );
  },
);
LoadingBlock.displayName = "LoadingBlock";
